// Pure helpers for the Scopes pane's History controls ("Recheck History").
// No imports on purpose: scripts/run-tests.mjs compiles each lib module alone.

export interface CountRow { checked: number; changed: number; unreadable?: number }
export interface PreviewShape {
  tables: { packets: CountRow; corescope_packets: CountRow; repeater_link_events: CountRow };
  by_scope: Record<string, { packets: number; corescope_packets: number; link_events: number }>;
  total_changes: number;
  resumable_run_id?: string | null;
}

/** 497 -> "497", 2213 -> "2,213", 1470000 -> "1.47M". */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  return Math.round(n).toLocaleString("en-US");
}

export interface PreviewTableRow { label: string; checked: string; change: string; unreadable: string }

export function previewTableRows(p: PreviewShape): PreviewTableRow[] {
  const t = p.tables;
  return [
    { label: "Packets", checked: formatCount(t.packets.checked), change: formatCount(t.packets.changed), unreadable: formatCount(t.packets.unreadable ?? 0) },
    { label: "CoreScope Packets", checked: formatCount(t.corescope_packets.checked), change: formatCount(t.corescope_packets.changed), unreadable: formatCount(t.corescope_packets.unreadable ?? 0) },
    { label: "Link Events", checked: formatCount(t.repeater_link_events.checked), change: formatCount(t.repeater_link_events.changed), unreadable: "-" },
  ];
}

export interface ScopeTableRow { scope: string; packets: string; corescope: string; linkEvents: string }

/** One row per scope name, busiest first, then by name; no truncation. Changed rows whose new result
 * names no single scope (for example None becoming Ambiguous) go to a final "Other" row, so each column
 * adds up to its table total. */
export function scopeTableRows(p: PreviewShape): ScopeTableRow[] {
  const sums = { packets: 0, corescope: 0, linkEvents: 0 };
  for (const v of Object.values(p.by_scope)) {
    sums.packets += v.packets;
    sums.corescope += v.corescope_packets;
    sums.linkEvents += v.link_events;
  }
  const other = {
    packets: Math.max(0, p.tables.packets.changed - sums.packets),
    corescope: Math.max(0, p.tables.corescope_packets.changed - sums.corescope),
    linkEvents: Math.max(0, p.tables.repeater_link_events.changed - sums.linkEvents),
  };
  const rows = Object.entries(p.by_scope)
    .sort((a, b) => {
      const ta = a[1].packets + a[1].corescope_packets + a[1].link_events;
      const tb = b[1].packets + b[1].corescope_packets + b[1].link_events;
      return tb - ta || a[0].localeCompare(b[0]);
    })
    .map(([scope, v]) => ({
      scope,
      packets: formatCount(v.packets),
      corescope: formatCount(v.corescope_packets),
      linkEvents: formatCount(v.link_events),
    }));
  if (other.packets || other.corescope || other.linkEvents) {
    rows.push({ scope: "Other", packets: formatCount(other.packets), corescope: formatCount(other.corescope), linkEvents: formatCount(other.linkEvents) });
  }
  return rows;
}

/** Apply is only possible when the preview found something to change. */
export function canApply(p: PreviewShape | null | undefined, busy: boolean): boolean {
  return !!p && (p.total_changes > 0 || !!p.resumable_run_id) && !busy;
}

/** Nothing to write and no interrupted run to finish. */
export function nothingToChange(p: PreviewShape | null | undefined): boolean {
  return !!p && p.total_changes === 0 && !p.resumable_run_id;
}

/** "Oct 12 09:14" in the given zone (browser zone by default). */
export function formatStamp(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone,
  }).formatToParts(d);
  const get = (type: string) => parts.find((x) => x.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("month")} ${get("day")} ${hour}:${get("minute")}`;
}

/** "09:12" for the Counted line. */
export function formatClock(iso: string | null | undefined, timeZone?: string): string {
  const s = formatStamp(iso, timeZone);
  return s === "-" ? s : s.split(" ").slice(2).join(" ");
}

export function percent(progress: { done: number; total: number } | undefined): number {
  if (!progress || progress.total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((progress.done / progress.total) * 100)));
}

/** One bar per table row of the preview. A table that has started with nothing to do counts as complete. */
export function applyStages(progress: Record<string, { done: number; total: number }>): { packets: number; corescope: number; linkEvents: number } {
  const bar = (key: string) => {
    const v = progress[key];
    if (!v) return 0;
    return v.total <= 0 ? 100 : percent(v);
  };
  return { packets: bar("packets"), corescope: bar("corescope_packets"), linkEvents: bar("link_events") };
}

export function changedSummary(c: { packets: number; corescope_packets: number; repeater_link_events: number }): string {
  return `${formatCount(c.packets)} Packets, ${formatCount(c.corescope_packets)} CoreScope, ${formatCount(c.repeater_link_events)} Link Events`;
}

export function recomputeLabel(value: string | undefined): string {
  switch (value) {
    case "requested": return "Requested";
    case "failed": return "Failed";
    case "not_requested": return "Not Requested";
    default: return value ? value.charAt(0).toUpperCase() + value.slice(1) : "Not Requested";
  }
}

/** Undo is offered for a run that was applied (or stopped partway) and not yet undone. */
export function canUndo(last: { state: string; undone: boolean } | null | undefined, busy: boolean): boolean {
  return !!last && !last.undone && last.state !== "undone" && !busy;
}

export type ErrorKind = "stale" | "message";

/** Maps an API refusal to the label the pane shows. */
export function refusalLabel(status: number | undefined, detail: string): { kind: ErrorKind; text: string } {
  if (status === 409 && /scope names changed/i.test(detail)) return { kind: "stale", text: "Scope Names Changed" };
  if (status === 409 && /expired/i.test(detail)) return { kind: "stale", text: "Preview Expired" };
  if (status === 409 && /nothing to change/i.test(detail)) return { kind: "message", text: "Nothing To Change" };
  if (status === 409 && /already running/i.test(detail)) return { kind: "message", text: "Already Running" };
  if (status === 409 && /undo unfinished/i.test(detail)) return { kind: "message", text: "Undo Unfinished" };
  if (status === 403) return { kind: "message", text: "Admin Only" };
  return { kind: "message", text: "Failed" };
}

/** Tag next to Last Run: only a run that stopped partway gets one. */
export function runTag(state: string | undefined): string | null {
  return state === "interrupted" ? "Interrupted" : null;
}

/** Stamp for Last Run: a run that never finished shows when it started. */
export function lastRunStamp(last: { finished_at: string | null; started_at: string | null } | null | undefined, timeZone?: string): string {
  if (!last) return "-";
  return formatStamp(last.finished_at ?? last.started_at, timeZone);
}

/** Polling keeps going through a dropped connection or a server hiccup, but not through a real refusal. */
export function isTransientPollError(status: number | undefined): boolean {
  return status === undefined || status >= 500;
}

export const POLL_RETRY_LIMIT = 5;
